import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import {
	getCoreRowModel,
	type ColumnDef,
	flexRender,
	useReactTable
} from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { StatusChip } from '@/components/Chips/StatusChip/StatusChip';
import { Modal } from '@/components/Modal/Modal';
import { SectionField } from '@/components/SectionField/SectionField';
import { Dcr, DcrType } from '@/core/graphql/generated/types';
import type { DcrAttachment } from '@/features/DataChangeRequest/_types/attachment';
import { AttachmentGrid } from '@/features/DataChangeRequest/Attachments/AttachmentGrid/AttachmentGrid';
import { ProfileMetaData } from '@/pages/HcpProfilePage/ProfileMetaData/ProfileMetaData';
import { computeDcrStatus } from '@/pages/MyDCRsPage/_utils/computeDcrStatus';
import { formatDate } from '@/utils/formatters/formatData';

import { isChangeRow } from '../_utils/isChangeRow';
import { toLocalAttachments } from '../_utils/toLocalAttachments';

import { useDcr, TableRowType } from './_hooks/useDcr';
import { DCRFieldName } from './DCRFieldName/DCRFieldName';
import { DCRTableHeader } from './DCRTableHeader/DCRTableHeader';
import { viewDcrModalStyles as sx } from './ViewDCRModal.styles';

type Props = {
	dcr: Dcr | null;
	open: boolean;
	onClose: () => void;
};

export const ViewDCRModal = ({ dcr, open, onClose }: Props) => {
	const { t } = useTranslation();
	const { data, profile, countryId } = useDcr(dcr);

	const columns: ColumnDef<TableRowType>[] = useMemo(
		() => [
			{
				id: 'field',
				header: t('Name'),
				accessorKey: 'field',
				minSize: 200,
				cell: ({ row }) =>
					isChangeRow(row.original) ? (
						<DCRFieldName
							field={row.original.field}
							type={row.original.type}
							countryId={countryId}
						/>
					) : (
						'-'
					)
			},
			...(dcr?.type === DcrType.Change
				? [
						{
							header: t('Current'),
							accessorKey: 'previousValue',
							minSize: 150,
							accessorFn: (row: TableRowType) =>
								isChangeRow(row) ? row.previousValue : null
						}
					]
				: []),
			{
				header: t('Requested'),
				accessorKey: 'requestedValue',
				minSize: 150,
				accessorFn: row => (isChangeRow(row) ? row.requestedValue : null)
			},
			{
				header: t('Final'),
				accessorKey: 'finalValue',
				minSize: 150,
				accessorFn: row => (isChangeRow(row) ? row.finalValue : null)
			},
			{
				header: t('Status'),
				accessorKey: 'result',
				size: 100,
				minSize: 100,
				maxSize: 100,
				enableResizing: false,
				cell: ({ row }) => (
					<StatusChip
						status={(isChangeRow(row.original) && row.original.result) || null}
					/>
				)
			}
		],
		[t, dcr, countryId]
	);

	// eslint-disable-next-line react-hooks/incompatible-library
	const table = useReactTable({
		data,
		columns,
		enableColumnResizing: true,
		columnResizeMode: 'onChange',
		getCoreRowModel: getCoreRowModel(),
		debugTable: true,
		debugHeaders: true,
		debugColumns: true
	});

	if (!dcr) return null;

	const attachments: DcrAttachment[] = toLocalAttachments(dcr.attachments);

	return (
		<Modal
			open={open}
			onClose={onClose}
			size='xl'
			customSx={{ titleRow: sx.modalTitleRow }}
			title={
				<Box sx={sx.modalHeader}>
					<ProfileMetaData
						profile={profile}
						customSx={{
							root: { p: 0 },
							nameInfoContainer: { p: 0 }
						}}
						withoutHeader
						withProfileLink={!!dcr.currentEntity}
						withInactive
						withInternationalName
						withAffiliation
					/>
				</Box>
			}
		>
			<Box sx={sx.modalBody}>
				<Box sx={sx.dcrMetaData(table.getCenterTotalSize())}>
					<SectionField label={t('Status')}>
						<StatusChip status={computeDcrStatus(dcr)} showHelp />
					</SectionField>
					<SectionField
						label={t('Submitted')}
						value={dcr.createdAt && formatDate(dcr.createdAt)}
					/>
					<SectionField
						label={t('Resolved')}
						value={dcr.completedAt && formatDate(dcr.completedAt)}
					/>
					<SectionField label={t('Requester Notes')} value={dcr.note} />
					<SectionField
						label={t('Resolution Notes')}
						value={dcr.resolutionNotes?.map(note => note.comment)}
					/>
					<SectionField label={t('DCR ID')} value={String(dcr.networkId)} />
					{attachments.length > 0 && (
						<AttachmentGrid attachments={attachments} />
					)}
				</Box>

				<Table
					{...{
						style: {
							width: table.getCenterTotalSize(),
							minWidth: '100%'
						}
					}}
				>
					<TableBody>
						{table.getRowModel().rows.map(row => {
							if ('label' in row.original) {
								return (
									<DCRTableHeader
										key={row.id}
										table={table}
										label={row.original.label}
									/>
								);
							}

							return (
								<TableRow key={row.id} sx={sx.row}>
									{row.getVisibleCells().map(cell => (
										<TableCell
											key={cell.id}
											sx={sx.cell(cell.column.getSize())}
										>
											{flexRender(
												cell.column.columnDef.cell,
												cell.getContext()
											)}
										</TableCell>
									))}
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			</Box>
		</Modal>
	);
};
